import { useMemo } from "react";
import type { StaggerRow } from "../../hooks/useForecastData";
import { HEX } from "../../lib/colorSystem";
import { formatUSD } from "@/utils/formatters";

// =============================================================================
// STAGGER CHART — Grove-style forecast evolution table
// =============================================================================
// Rows = month forecast was made, Columns = month being forecasted.
// Asterisk (*) marks actual confirmed values. Reading down a column shows
// how predictions for that month evolved over time.
// =============================================================================

interface StaggerChartProps {
  stagger: StaggerRow[];
  metric: "revenue_closed" | "cash_collected";
  /** Active color for highlighted cells — blue for cash, red/orange for revenue */
  activeColor?: string;
}

function getMonthLabel(dateStr: string): string {
  try {
    const d = new Date(dateStr + "T00:00:00");
    const month = d.toLocaleString("default", { month: "short" });
    const year = d.getFullYear().toString().slice(-2);
    return `${month} '${year}`;
  } catch {
    return dateStr;
  }
}

export default function StaggerChart({
  stagger,
  metric,
  activeColor = "var(--accent)",
}: StaggerChartProps) {
  // Extract unique target months (columns) from all forecast rows
  const columns = useMemo(() => {
    const months = new Set<string>();
    stagger.forEach((row) => {
      Object.keys(row.forecasts).forEach((key) => {
        const cell = row.forecasts[key];
        if (cell.metric === metric) {
          months.add(cell.for_month);
        }
      });
    });
    return Array.from(months).sort();
  }, [stagger, metric]);

  if (stagger.length === 0) {
    return (
      <div
        className="liquid-glass anim-lift rounded-[14px] flex flex-col items-center justify-center gap-3"
        style={{ padding: "40px", minHeight: 200 }}
      >
        <svg
          className="w-10 h-10 text-glass-muted opacity-40"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5"
          />
        </svg>
        <span className="text-sm text-glass-muted">No forecast data yet</span>
        <span className="text-xs text-glass-inactive">
          Set a monthly cash goal in the Cash Planner below — it auto-populates here.
          <br />
          Each row = when the forecast was made. Each column = the month being forecasted.
          <br />
          Reading down a column shows how predictions evolved over time.
        </span>
      </div>
    );
  }

  return (
    <div
      className="liquid-glass anim-lift rounded-[14px] overflow-hidden"
      style={{ padding: "20px 22px" }}
    >
      <div className="overflow-x-auto">
        <table className="w-full" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
          <thead>
            <tr>
              <th
                className="text-left font-medium"
                style={{
                  fontSize: 11,
                  color: "var(--text-tertiary)",
                  padding: "8px 12px",
                  borderBottom: "1px solid var(--border)",
                }}
              >
                Forecast Made
              </th>
              {columns.map((col) => (
                <th
                  key={col}
                  className="text-right font-medium"
                  style={{
                    fontSize: 11,
                    color: "var(--text-tertiary)",
                    padding: "8px 12px",
                    borderBottom: "1px solid var(--border)",
                    whiteSpace: "nowrap",
                  }}
                >
                  {getMonthLabel(col)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {stagger.map((row, idx) => {
              const madeMonth = row.forecast_made_month;
              return (
                <tr
                  key={madeMonth}
                  className="anim-pop"
                  style={{
                    animationDelay: `${Math.min(idx, 15) * 100}ms`,
                    animationDuration: "480ms",
                  }}
                >
                  <td
                    className="font-semibold"
                    style={{
                      fontSize: 12,
                      color: "var(--text-primary)",
                      padding: "10px 12px",
                      borderBottom: "1px solid var(--border-light)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {getMonthLabel(madeMonth)}
                  </td>
                  {columns.map((col) => {
                    const key = `${col}|${metric}`;
                    const cell = row.forecasts[key];
                    const isActual = madeMonth === col;
                    const hasBoth = cell && cell.actual !== null && !isActual;

                    // Variance: green if actual > predicted, red if below
                    let varianceColor = "transparent";
                    if (hasBoth && cell.actual !== null) {
                      varianceColor =
                        cell.actual >= cell.predicted
                          ? `color-mix(in srgb, ${HEX.green} 8%, transparent)`
                          : `color-mix(in srgb, ${HEX.red} 8%, transparent)`;
                    }

                    return (
                      <td
                        key={col}
                        className="text-right"
                        style={{
                          padding: "10px 12px",
                          borderBottom: "1px solid var(--border-light)",
                          background: isActual
                            ? `color-mix(in srgb, ${activeColor} 6%, transparent)`
                            : varianceColor,
                        }}
                      >
                        {!cell ? (
                          <span style={{ color: "var(--text-tertiary)", fontSize: 12 }}>—</span>
                        ) : isActual ? (
                          <span className="font-bold" style={{ fontSize: 13, color: activeColor }}>
                            *{formatUSD(cell.actual ?? cell.predicted)}
                          </span>
                        ) : (
                          <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                            {formatUSD(cell.predicted)}
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div
        className="flex items-center gap-4 mt-3 pt-3"
        style={{ borderTop: "1px solid var(--border-light)" }}
      >
        <div className="flex items-center gap-1.5">
          <div
            className="rounded"
            style={{
              width: 12,
              height: 12,
              background: `color-mix(in srgb, ${activeColor} 6%, transparent)`,
              border: `1px solid ${activeColor}`,
            }}
          />
          <span style={{ fontSize: 10, color: "var(--text-tertiary)" }}>Actual (confirmed)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div
            className="rounded"
            style={{
              width: 12,
              height: 12,
              background: `color-mix(in srgb, ${HEX.green} 8%, transparent)`,
              border: `1px solid ${HEX.green}`,
            }}
          />
          <span style={{ fontSize: 10, color: "var(--text-tertiary)" }}>Beat forecast</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div
            className="rounded"
            style={{
              width: 12,
              height: 12,
              background: `color-mix(in srgb, ${HEX.red} 8%, transparent)`,
              border: `1px solid ${HEX.red}`,
            }}
          />
          <span style={{ fontSize: 10, color: "var(--text-tertiary)" }}>Missed forecast</span>
        </div>
      </div>
    </div>
  );
}
